import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { SchemaSection, SchemaScoreField, SchemaGeneratorState, JSONSchema } from '@/api/types/schema'
import { 
  generateSchemaFromSections, 
  extractSectionsFromSchema, 
  createNewSection, 
  createNewScore
} from '@/utils/schema/schema-utils'
import { v4 as uuidv4 } from 'uuid'

interface UseSchemaStateProps {
  currentSchema?: JSONSchema
  onSchemaChange: (schema: JSONSchema) => void
}

export function useSchemaState({ 
  currentSchema, 
  onSchemaChange
}: UseSchemaStateProps) {
  // Track if we're initializing to prevent calling onSchemaChange during init
  const isInitializing = useRef(true)
  const lastSchemaRef = useRef<JSONSchema | null>(null)
  
  // Schema generator state
  const [generatorState, setGeneratorState] = useState<SchemaGeneratorState>(() => ({
    sections: currentSchema ? extractSectionsFromSchema(currentSchema) : [],
    dialogTitle: String(currentSchema?.properties?.title?.const || 'Analysis Results'),
    isDirty: false
  }))

  // Generate schema from current state
  const generatedSchema = useMemo(() => {
    if (generatorState.sections.length === 0) {
      return null
    }
    return generateSchemaFromSections(generatorState.sections, generatorState.dialogTitle)
  }, [generatorState.sections, generatorState.dialogTitle])

  // Update parent when schema changes (but not during initialization)
  useEffect(() => {
    if (generatedSchema && !isInitializing.current) {
      // Only update if the schema actually changed
      const currentSchemaString = JSON.stringify(generatedSchema)
      const lastSchemaString = lastSchemaRef.current ? JSON.stringify(lastSchemaRef.current) : null
      
      if (currentSchemaString !== lastSchemaString) {
        lastSchemaRef.current = generatedSchema
        onSchemaChange(generatedSchema)
      }
    }
  }, [generatedSchema, onSchemaChange])

  // Initialize from current schema when it changes
  useEffect(() => {
    if (currentSchema) {
      const sections = extractSectionsFromSchema(currentSchema)
      const dialogTitle = String(currentSchema.properties?.title?.const || 'Analysis Results')
      
      // Only update state if it's actually different to prevent unnecessary re-renders
      setGeneratorState(prev => {
        const sectionsChanged = JSON.stringify(prev.sections) !== JSON.stringify(sections)
        const titleChanged = prev.dialogTitle !== dialogTitle
        
        if (sectionsChanged || titleChanged) {
          return {
            sections,
            dialogTitle,
            isDirty: false
          }
        }
        return prev
      })
      
      // Mark initialization as complete after first render
      if (isInitializing.current) {
        setTimeout(() => {
          isInitializing.current = false
        }, 0)
      }
    }
  }, [currentSchema])

  // Sort sections by order field
  const sortedSections = useMemo(() => {
    return [...generatorState.sections].sort((a, b) => a.order - b.order)
  }, [generatorState.sections])

  const handleSectionChange = useCallback((sectionUuid: string, updates: Partial<SchemaSection>) => {
    isInitializing.current = false // Mark as user-initiated change
    const updatedSections = generatorState.sections.map(section => 
      section.uuid === sectionUuid 
        ? { ...section, ...updates }
        : section
    )
    setGeneratorState(prev => ({
      ...prev,
      sections: updatedSections,
      isDirty: true
    }))
  }, [generatorState.sections])

  const handleSectionsChange = useCallback((sections: SchemaSection[]) => {
    isInitializing.current = false // Mark as user-initiated change
    setGeneratorState(prev => ({
      ...prev,
      sections,
      isDirty: true
    }))
  }, [])

  const handleDialogTitleChange = useCallback((dialogTitle: string) => {
    isInitializing.current = false // Mark as user-initiated change
    setGeneratorState(prev => ({
      ...prev,
      dialogTitle,
      isDirty: true
    }))
  }, [])

  const addSection = useCallback(() => {
    const newSection = createNewSection('New Section', generatorState.sections)
    handleSectionsChange([...generatorState.sections, newSection])
  }, [generatorState.sections, handleSectionsChange])

  const removeSection = useCallback((sectionId: string) => {
    const updatedSections = generatorState.sections.filter(section => section.id !== sectionId)
    handleSectionsChange(updatedSections)
  }, [generatorState.sections, handleSectionsChange])

  const duplicateSection = useCallback((sectionId: string) => {
    const sectionToDuplicate = generatorState.sections.find(s => s.id === sectionId)
    if (!sectionToDuplicate) return
    
    const maxOrder = Math.max(...generatorState.sections.map(s => s.order), 0)
    const newId = `${sectionToDuplicate.id}_copy_${Date.now()}`
    const newTitle = `${sectionToDuplicate.title} (Copy)`
    const newOrder = maxOrder + 1
    
    const duplicatedSection: SchemaSection = {
      ...sectionToDuplicate,
      uuid: uuidv4(), // Generate new UUID for the copy
      id: newId,
      title: newTitle,
      order: newOrder
    }
    
    handleSectionsChange([...generatorState.sections, duplicatedSection])
  }, [generatorState.sections, handleSectionsChange])

  const addScore = useCallback((sectionUuid: string) => {
    const section = generatorState.sections.find(s => s.uuid === sectionUuid)
    if (!section) return
    
    const newScore = createNewScore('New Score', section.scores)
    const updatedSection = {
      ...section,
      scores: [...section.scores, newScore]
    }
    
    handleSectionChange(section.uuid, updatedSection)
  }, [generatorState.sections, handleSectionChange])

  const removeScore = useCallback((sectionUuid: string, scoreId: string) => {
    const section = generatorState.sections.find(s => s.uuid === sectionUuid)
    if (!section) return
    
    const updatedScores = section.scores.filter(score => score.id !== scoreId)
    handleSectionChange(sectionUuid, { scores: updatedScores })
  }, [generatorState.sections, handleSectionChange])

  const updateScore = useCallback((sectionUuid: string, scoreId: string, updates: Partial<SchemaScoreField>) => {
    const section = generatorState.sections.find(s => s.uuid === sectionUuid)
    if (!section) return
    
    const updatedScores = section.scores.map(score =>
      score.id === scoreId ? { ...score, ...updates } : score
    )
    handleSectionChange(sectionUuid, { scores: updatedScores })
  }, [generatorState.sections, handleSectionChange])

  return {
    generatorState,
    sortedSections,
    generatedSchema,
    handleSectionChange,
    handleSectionsChange,
    handleDialogTitleChange,
    addSection,
    removeSection,
    duplicateSection,
    addScore,
    removeScore,
    updateScore
  }
}
